import React from 'react';
import { Row, Col, Form, Button, Switch, Divider, Tooltip } from 'antd';
import { connect } from 'react-redux';
import { FormInstance } from 'antd/lib/form';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { RootState } from '../../../slices/rootReducer';
import {
  selectors as featureListSelectors,
  actions as featureListActions
} from '../../../slices/features/list';
import { ReactComponent as SxInfo } from '../../../assets/sightx-icons/svg/sx-circle-info.svg';
import { updateAccountPolicy } from '../../../slices/accountPolicy/form.thunks';
import { IAccount, IFormPermission, PermissionAccessTypes } from '../../../slices/accounts/account';
import { UpdateAccountPolicy, USE_DEFAULT } from '../../../slices/accountPolicy/accountFeature';
import { isEmpty } from '../../../utils/utils';
import { isIncrowdClient } from '../../../utils/clients';
import { selectors as authSelectors } from '../../../slices/auth';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { UserTypes } from '../../common/domain/userTypes';
import { IUser } from '../../../slices/users/user';
import { IFetchPermissionsRequest } from '../../../slices/features/list.thunks';

import styles from './accountFeatures.module.scss';

interface IFeaturesProps {
  formFeatures: IFormPermission[];
  showError: boolean;
}

interface IStateProps {
  features: IFormPermission[];
  loading: boolean;
  loggedUser: IUser | null;
}

interface IDispatchProps {
  fetchFeaturesList: (payload: IFetchPermissionsRequest) => void;
  updateAccountPolicy: (payload: UpdateAccountPolicy) => void;

  initialValues?: IAccount;
}

class AccountFeaturesBase extends React.Component<IDispatchProps & IStateProps, IFeaturesProps> {
  private formRef: React.RefObject<FormInstance>;

  constructor(props: IDispatchProps & IStateProps) {
    super(props);
    this.state = {
      showError: false,
      formFeatures: []
    };
    this.formRef = React.createRef<FormInstance>();
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount(): void {
    this.initFilterFeaturesList();
  }

  componentDidUpdate(prevProps: IStateProps & IDispatchProps): void {
    if (isEmpty(prevProps.features) && !isEmpty(this.props.features)) {
      this.setState( { formFeatures: this.filterAccountPermissions() } );
    }
  }

  private initFilterFeaturesList(): void {
    const { fetchFeaturesList , initialValues: { accessType } = {} } = this.props;
    if (accessType) {
      fetchFeaturesList({ accessType });
    }
  }

  private onChangeSwitch( name:string, checked: boolean): void {
    const { formFeatures } = this.state;
    const updatedItems: IFormPermission[] = formFeatures.map(item => (item.name === name) ? { ...item, checked: !checked } : item);
    this.setState( { formFeatures: updatedItems } );
  }

  private async handleSubmit(): Promise<void> {
    const { initialValues, updateAccountPolicy } = this.props;
    const { formFeatures } = this.state;
    const accountFeatureFilter: UpdateAccountPolicy['permissions'] = formFeatures.map( item => {
      return { name: item.name, accessLevel: item.checked ? PermissionAccessTypes.Available : USE_DEFAULT };
    });

    const values: UpdateAccountPolicy = {
      accountId: initialValues?.id || '',
      permissions: accountFeatureFilter
    };

    await updateAccountPolicy(values);
  }

  private filterAccountPermissions(): IFormPermission[] {
    const { initialValues, features, loggedUser } = this.props;
    const hiddenFeatures = ['datasheet', 'sightxPanel', 'salesforce', 'surveyAddons'];
    const shouldHideFeatures = isIncrowdClient() && loggedUser?.userType !== UserTypes.SxAdmin;
    const filteredPermissions = shouldHideFeatures ? features.filter(feature => !hiddenFeatures.includes(feature.name)) : features;

    return filteredPermissions.map((item) => {
      return {
        ...item,
        checked: !!initialValues?.permissions?.some( accountFeature => accountFeature.name === item.name && accountFeature.accessLevel === PermissionAccessTypes.Available ),
        userFriendlyName: item.label || item.name
      };
    });
  }

  private renderFeatures(formFeatures: IFormPermission[]) {
    return <>
      {formFeatures.map( (item) => {
        return <Form.Item name={item.name} className={styles.formItem} >
          <div className={styles.formContainer}>
            <span className={styles.nameFeature} >{item.userFriendlyName}</span>
            <div className={styles.actionsContainer}>
              <Tooltip title={item.description} >
                <Icon component={SxInfo} />
              </Tooltip>
              <Switch
                size="small"
                onChange={ () => this.onChangeSwitch( item.name, item.checked ) }
                checked={item.checked}
                className={styles.switch}
              />
            </div>
          </div>
          <Divider className='divider' />
        </Form.Item>;
      })}
    </>;

  }

  private renderError() {
    const { showError } = this.state;
    if (!showError) {
      return null;
    }

    return (
      <Col span={24}>
        <p className={styles.errorMessage}></p>
      </Col>
    );
  }

  private renderLoadingSpinner() {
    const antIcon = <LoadingOutlined style={{ fontSize: 44 }} spin />;
    return (
      <Spin indicator={antIcon} className={styles.loading} />
    );
  }

  render(): React.ReactNode {
    if (!isFeatureEnabled(Features.PlgExternal)) {
      return null;
    }

    const { formFeatures } = this.state;
    const { loading } = this.props;

    if(loading) {
      return this.renderLoadingSpinner();
    }
    return (
      <Form
        ref={this.formRef}
        onFinish={this.handleSubmit}
        colon={false}
        layout="vertical"
      >
        {this.renderFeatures(formFeatures)}
        {this.renderError()}

        <Row justify="end" className={styles.actionWrapper} >
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              className={`ant-btn-primary ${styles.submitButton}`}
            >
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    features: featureListSelectors.getList(state),
    loading: featureListSelectors.getLoading(state),
    loggedUser: authSelectors.getLoggedInUser(state)
  } as IStateProps;
};

const mapDispatchToProps = {
  fetchFeaturesList: featureListActions.fetchFeaturesList,
  updateAccountPolicy
};

export const AccountFeatures = connect(mapStateToProps,mapDispatchToProps)(AccountFeaturesBase);
